import React, { useCallback, useEffect, useState } from 'react';
import IMenu from '../../interfaces/menu';
import MenuItemsLoader from '../loading/menuLoader';
import { useTranslation } from 'react-i18next';
import { MenuItemDto } from '../../api-types';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectActiveUser } from '../../app/userSlice';
import useUserSettings from '../../hooks/useUserSettings';
import Modal from 'react-modal';
import { extendedModalStyles } from '../../config/modal';
import { MenuCategory } from '../menuCategory';
import {
    changeMenuType,
    removePreparedConstantMenuItems,
    removePreparedMenuItems,
    selectMenuType,
    selectPreparedConstantMenuItems,
    selectPreparedMenuItems
} from '../../app/menuSlice';
import { useGetGroupsQuery, useGetMenuQuery } from '../../api/menuApi';
import { MenuItemType } from '../../api-types/models/MenuItemDto';
import { selectConfig } from '../../app/configSlice';
import { LoginType } from '../../api-types/models/LoginDto';
import { Prompt } from '../prompt';
import { EditModeWarning } from '../editModeWarning';
import { AddMenuItem } from '../addMenuItem';
import { selectDate } from '../../app/calendarSlice';
import { format, isPast, isToday } from 'date-fns';
import { isEmpty } from 'lodash';
import { selectAuthUser } from '../../app/authSlice';
import { getUserGroups } from '../../utils/user';
import { NavigationTabs } from '../navigationTabs';
import { DateSelector } from '../dateSelector';
import { formatDate } from '../../utils/date';

import './index.css';
import { CustomEvent } from '@piwikpro/react-piwik-pro';
import { capitalizeFirstLetter } from 'utils/capitalizeCase';

interface IProps {
    showSuccessToast: boolean;
    setShowSuccessToast(value: boolean): void;
    loginType: string | null;
    date: Date;
}

export const Menu: React.FunctionComponent<IMenu & IProps> = ({
    showSuccessToast,
    setShowSuccessToast,
    loginType,
    date
}: IProps) => {
    const dispatch = useAppDispatch();

    const [time, setTime] = useState<Date>(new Date());
    const [isAddMenuItemModalOpen, setIsAddMenuItemModalOpen] = useState(false);

    const { data: userSettings } = useUserSettings();
    const activeUser = useAppSelector(selectActiveUser);
    const menuItemType = useAppSelector(selectMenuType);
    const config = useAppSelector(selectConfig);
    const preparedMenuItems = useAppSelector(selectPreparedMenuItems);
    const selectedDate = useAppSelector(selectDate);
    const user = useAppSelector(selectAuthUser);
    const preparedMenuConstantItems = useAppSelector(
        selectPreparedConstantMenuItems
    );

    const dateIso = formatDate(date);
    const { data: menuItems, isLoading: isMenuLoading } = useGetMenuQuery(
        dateIso,
        { skip: !config }
    );
    const { data: groups = [] } = useGetGroupsQuery(undefined, {
        skip: !config
    });

    const { t } = useTranslation();

    const handleChangeMenuItemType = useCallback((selectedMenuType: string) => {
        dispatch(changeMenuType(selectedMenuType));
    }, []);

    const openAddMenuItemModal = useCallback(() => {
        setIsAddMenuItemModalOpen(true);
    }, []);

    // Live countdown
    useEffect(() => {
        const interval = setInterval(() => setTime(new Date()), 1000);

        return () => clearInterval(interval);
    }, [time]);

    useEffect(() => {
        CustomEvent.trackEvent(
            'page_view',
            `Menu ${
                loginType === LoginType.BRUNCH ? '(Brunch)' : ''
            } - ${capitalizeFirstLetter(menuItemType.toLowerCase())}`
        );
    }, [menuItemType]);

    if (
        isMenuLoading ||
        (user &&
            !getUserGroups(user).includes(LoginType.BRUNCH) &&
            !userSettings &&
            !menuItems?.length)
    ) {
        return (
            <div className='loading'>
                <MenuItemsLoader />
            </div>
        );
    }

    let menuItemsCopy = menuItems ?? [];

    if (loginType !== LoginType.BRUNCH && userSettings?.foodFilter) {
        // TODO: Add icecream to Profile page to filter it out
        const filteredTags: string[] = Object.keys(
            userSettings.foodFilter
        ).filter(
            (tag) =>
                userSettings.foodFilter[
                    tag as keyof typeof userSettings.foodFilter
                ] === true ||
                tag === 'SELF_SERVED' ||
                tag === 'ICE_CREAM'
        );

        // Apply filter on menu only if the user is not guest
        if (activeUser === undefined && menuItemType === MenuItemType.KITCHEN) {
            menuItemsCopy = menuItemsCopy.filter((menuItem: MenuItemDto) =>
                menuItem.category
                    ? filteredTags.includes(menuItem.category)
                    : true
            );
        }

        const filteredAllergens: string[] = Object.keys(
            userSettings.allergies
        ).filter(
            (allergen) =>
                userSettings.allergies[
                    allergen as keyof typeof userSettings.allergies
                ] === true
        );

        // Apply filter on menu only if the user is not guest
        if (activeUser === undefined) {
            menuItemsCopy = menuItemsCopy.filter((menuItem: MenuItemDto) => {
                return !filteredAllergens.length
                    ? true
                    : menuItem.allergens.every(
                          (allergen: string) =>
                              !filteredAllergens.includes(allergen)
                      );
            });
        }
    }

    if (
        menuItems &&
        !menuItems.length &&
        !getUserGroups(user).includes(LoginType.BRUNCH)
    ) {
        return (
            <div className='no-menu'>
                <div className='content'>
                    <h1>{t('no_menu_title')}</h1>
                    <p>{t('no_menu_description')}</p>
                </div>
                <div className='image'>
                    <img src='/images/no-menu.svg' alt='no-menu' />
                </div>
            </div>
        );
    }

    if (
        !menuItemsCopy.length &&
        !getUserGroups(user).includes(LoginType.BRUNCH)
    ) {
        return (
            <div className='wrong-filter'>
                <div className='content'>
                    <h1>{t('wrong_filter_title')}</h1>
                    <p>{t('wrong_filter_description')}</p>
                </div>
                <div className='image'>
                    <img src='/images/no-menu.svg' alt='no-menu' />
                </div>
            </div>
        );
    }

    const preparedMenuItemsForToday = Object.keys(preparedMenuItems).includes(
        format(selectedDate, 'yyyy-MM-dd')
    )
        ? preparedMenuItems[format(selectedDate, 'yyyy-MM-dd')]
        : [];

    menuItemsCopy =
        [
            ...menuItemsCopy,
            ...preparedMenuItemsForToday,
            ...preparedMenuConstantItems
        ]?.filter((item) => item.costCenter === menuItemType) ?? [];

    return (
        <>
            <NavigationTabs
                activeTab={menuItemType}
                setActiveTab={handleChangeMenuItemType}
                tabs={Object.values(MenuItemType)}
            />
            <div className='menu'>
                <div className='menu-actions'>
                    {getUserGroups(user).includes(LoginType.BRUNCH) &&
                        (!isPast(selectedDate) || isToday(selectedDate)) && (
                            <div className='admin-actions'>
                                <button
                                    className='btn btn-gray'
                                    onClick={openAddMenuItemModal}
                                >
                                    + {t('add_menu')}
                                </button>
                            </div>
                        )}
                </div>

                {showSuccessToast && (
                    <div className='success-toast'>
                        <img
                            className='success-toast-img'
                            src='/images/ico_success.svg'
                            alt='Success'
                        />
                        <span className='menu-closed-text'>
                            {t('rating_success')}
                        </span>
                        <div
                            className='close-toast'
                            onClick={() => setShowSuccessToast(false)}
                        >
                            <img alt='close' src='/images/ico_close.svg' />
                        </div>
                    </div>
                )}

                <div className='menu-groups'>
                    {getUserGroups(user).includes(LoginType.BRUNCH) && (
                        <DateSelector />
                    )}
                    {groups.map((group) => (
                        <MenuCategory
                            key={group.name}
                            category={group.name}
                            menuItems={menuItemsCopy.filter(
                                (menuItems) =>
                                    menuItems.group?.toUpperCase() ===
                                    group.name.toUpperCase()
                            )}
                        />
                    ))}
                </div>
            </div>
            {(!isEmpty(preparedMenuItems) ||
                !isEmpty(preparedMenuConstantItems)) && (
                <EditModeWarning
                    cancelEditMode={() => {
                        dispatch(removePreparedMenuItems());
                        dispatch(removePreparedConstantMenuItems());
                    }}
                />
            )}
            {isAddMenuItemModalOpen && (
                <Modal
                    isOpen={isAddMenuItemModalOpen}
                    style={extendedModalStyles}
                    onRequestClose={() => setIsAddMenuItemModalOpen(false)}
                    ariaHideApp={false}
                >
                    <AddMenuItem
                        closeModal={() => setIsAddMenuItemModalOpen(false)}
                    />
                </Modal>
            )}
            {!isEmpty(preparedMenuItems) && <Prompt isBlocked={true} />}
        </>
    );
};

export default Menu;
